import { type FetchResult, type MutationHookOptions, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type {
  CreateNoteInput,
  CreateNoteMutation,
  CreateNoteMutationVariables,
  DeleteNoteMutation,
  NoteParentInput,
} from 'types/graphql';

const DELETE_NOTE_MUTATION = gql`
  mutation DeleteNoteMutation($id: Int!) {
    deleteNote(id: $id) {
      id
    }
  }
`;

export const CREATE_NOTE_MUTATION = gql`
  mutation CreateNoteMutation($input: CreateNoteInput!) {
    createNote(input: $input) {
      id
      text
      createdAt
      external
      membership {
        user {
          id
          firstName
          lastName
        }
      }
      purchaseOrderId
      salesOrderId
      lotId
      billId
    }
  }
`;

type NoteParentKey = keyof NoteParentInput;

const NOTE_PARENT_ON: { [K in NoteParentKey]: (id: number) => NoteParentInput } = {
  billId: (billId) => ({ billId }),
  buySellOrderId: (buySellOrderId) => ({ buySellOrderId }),
  contactId: (contactId) => ({ contactId }),
  lotId: (lotId) => ({ lotId }),
  productionRunId: (productionRunId) => ({ productionRunId }),
  purchaseOrderId: (purchaseOrderId) => ({ purchaseOrderId }),
  salesOrderId: (salesOrderId) => ({ salesOrderId }),
  workOrderId: (workOrderId) => ({ workOrderId }),
};

const isNoteParentKey = (key: string): key is NoteParentKey => Object.hasOwn(NOTE_PARENT_ON, key);

/** The hook's call shape, unchanged: the parent id beside `input`, as the deprecated argument list had it. */
export type CreateNoteCall = { [K in NoteParentKey]?: number | null } & { input: Omit<CreateNoteInput, 'parent'> };

/** Moves the one parent id into `input.parent` (TAS-3660 N9); keys outside the parent set were never sent. */
const toCreateNoteVariables = ({ input, ...parentIds }: CreateNoteCall): CreateNoteMutationVariables => {
  const parents = Object.entries(parentIds).flatMap(([key, id]) =>
    isNoteParentKey(key) && typeof id === 'number' ? [NOTE_PARENT_ON[key](id)] : []
  );
  if (parents.length > 1) throw new Error('Choose one parent for the note.');

  const [parent] = parents;
  return { input: parent ? { ...input, parent } : input };
};

/** `createNote` (P7-R2). Options pass through to `useMutation`; the operation name is unchanged. */
export function useCreateNote(options?: MutationHookOptions<CreateNoteMutation, CreateNoteMutationVariables>) {
  const [mutate, { loading, error }] = useMutation<CreateNoteMutation, CreateNoteMutationVariables>(
    CREATE_NOTE_MUTATION,
    options
  );
  const create = useCallback(
    async (call: CreateNoteCall): Promise<FetchResult<CreateNoteMutation>> =>
      mutate({ variables: toCreateNoteVariables(call) }),
    [mutate]
  );
  return { create, loading, error };
}

/** A persisted note id, or the temporary uuid of a comment whose create has not resolved yet. */
type DeleteNoteVariables = { id: number | string };

/**
 * `deleteNote` (P7-R2). `id` is a comment's id as the comments section holds it: a comment deleted
 * before its create resolves still carries its temporary uuid, which is sent as-is, as before.
 */
export function useDeleteNote() {
  const [mutate, { loading, error }] = useMutation<DeleteNoteMutation, DeleteNoteVariables>(DELETE_NOTE_MUTATION);
  const remove = useCallback((id: DeleteNoteVariables['id']) => mutate({ variables: { id } }), [mutate]);
  return { remove, loading, error };
}
